import * as React from 'react';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { AdminDataProjectDTO, ApiResponse, PatchDocument, ProjectApi, ProjectDTO } from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import FormProject from './FormProject';
import { Blocker, NavigateFunction, Params, useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import compare, { Patch } from '@mms-v3/patch-lib';
import { getDefaultProject } from '../../../Settings/emptyModels';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { Loading } from '../../system/Loading';
import { IntlShape, useIntl } from 'react-intl';

export function EditProjectPage(): ReactNode {
  const navigate: NavigateFunction = useNavigate();
  const location = useLocation();
  const params: Readonly<Params<string>> = useParams();
  const {actions} = useContext(AuthContext);
  const projectApi: ProjectApi = new ProjectApi().withMiddleware(getAuthMiddleware(actions));
  const [blockRequested, setBlockRequested] = React.useState<boolean>(false);
  const blocker: Blocker = useBlocker(useCallback((): boolean => blockRequested, [blockRequested]));
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();

  const [project, setProject] = useState<ProjectDTO>();
  const [modifiedModel, setModifiedModel] = useState<ProjectDTO>();
  const [saving, setSaving] = useState<boolean>(false);

  const updateField = (field: keyof ProjectDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: ProjectDTO): ProjectDTO => ({...state, [field]: value}));
  };

  const updateAdminField = (field: keyof AdminDataProjectDTO, value: any): void => {
    setBlockRequested(true);
    setModifiedModel((state: ProjectDTO): ProjectDTO => ({...state, adminDataProject: {...state.adminDataProject, [field]: value}}));
  };

  useEffect((): void => {
    if (blocker.state === 'blocked') {
      if (!blockRequested) return blocker.proceed();
      toast.confirm({
        header: intl.formatMessage({id: 'popup.unsavedInput.pageChange.headline'}),
        message: intl.formatMessage({id: 'popup.unsavedInput.pageChange.message'}),
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-danger',
        acceptLabel: intl.formatMessage({id: 'button.confirm'}),
        accept: (): void => {
          setModifiedModel(structuredClone(project));
          blocker.proceed();
        },
        rejectLabel: intl.formatMessage({id: 'button.abort'}),
        reject: (): void => blocker.reset(),
      });
    }
  }, [blocker.state]);

  useEffect((): void => setModifiedModel(structuredClone(project)), [project]);

  useEffect((): void => {
    if (params.id !== undefined) {
      const id: number = Number(params.id);
      if (isNaN(id)) {
        void navigate('/error/404');
        return;
      }
      void fetchData(id);
    } else {
      setProject(getDefaultProject());
    }
  }, [location.pathname]);


  const submit = async (): Promise<void> => {
    setSaving(true);
    try {
      let response: ApiResponse<ProjectDTO>;
      if (!modifiedModel.id) {
        response = await projectApi.createProjectRaw({projectDTO: modifiedModel});
      } else {
        const patch: Patch[] = compare(project, modifiedModel);
        response = await projectApi.updateProjectRaw({
          projectId: modifiedModel.id,
          patchDocument: patch as PatchDocument[],
        });
      }
      if (response.raw.ok) {
        toast.notify({severity: 'success', summary: intl.formatMessage({id: 'message.saving.success.message'})});
        setBlockRequested(false);
        const value: ProjectDTO = await response.value();
        setBlockRequested(false);
        void navigate(`/project/${value.id}`);
        return;
      }
      toast.error(response, 'message.saving.failed');
    } catch (response) {
      toast.error(response.response, 'message.saving.failed');
    }
    setSaving(false);
  };

  const fetchData = async (id: number): Promise<void> => {
    const response: ApiResponse<ProjectDTO> = await projectApi.getProjectReducedRaw({
      projectId: id,
      accept: 'application/project.full+json',
    });
    if (!response.raw.ok) {
      toast.notify({
        severity: 'error',
        summary: intl.formatMessage({id: 'message.loading.failed.title'}),
        detail: intl.formatMessage({id: 'message.loading.failed.message'}),
      });
      void navigate(`/error/${response.raw.status}`);
    }
    setProject(await response.value());
  };
  if (!modifiedModel) {
    return React.createElement(Loading, {});
  }
  return React.createElement(FormProject, { modifiedModel, submit, updateField, updateAdminField, saving });
}

export default EditProjectPage;
